/**
 * Variables modal. Authors declare scenario-level variables that conditions
 * (visibility, variants, destinations, feedback) and effects (entry, choice)
 * can reference.
 *
 * Operations:
 *   - Declare a new variable (form at the bottom): name + type + default
 *   - Edit an existing variable inline: rename, change type/default, edit
 *     description
 *   - Delete with cascade — destructive, gets confirmation prompt
 *
 * Rename and delete trigger the store's cascade through every condition and
 * effect in the scenario.
 */
import { useEffect, useState } from 'react';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import type { VariableDecl } from '@branchworks/shared';
import { useScenarioStore } from '../store/scenarioStore';
import { Modal } from './Modal';

type VarType = VariableDecl['type'];

const defaultForType = (t: VarType): VariableDecl['default'] =>
  t === 'number' ? 0 : t === 'boolean' ? false : '';

export function VariablesModal() {
  const scenario = useScenarioStore((s) => s.scenario);
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const addVariable = useScenarioStore((s) => s.addVariable);
  const updateVariable = useScenarioStore((s) => s.updateVariable);
  const renameVariable = useScenarioStore((s) => s.renameVariable);
  const deleteVariable = useScenarioStore((s) => s.deleteVariable);

  const variables = scenario.variables || {};
  const entries = Object.entries(variables);

  // Add-form state
  const [draftName, setDraftName] = useState('');
  const [draftType, setDraftType] = useState<VarType>('number');
  const [addError, setAddError] = useState('');

  const tryAdd = () => {
    const result = addVariable(draftName, {
      type: draftType,
      default: defaultForType(draftType),
      description: '',
    });
    if (!result.ok) {
      setAddError(result.error);
      return;
    }
    setDraftName('');
    setAddError('');
  };

  return (
    <Modal
      kicker="Scenario configuration"
      title="Variables"
      onClose={() => setOpenModal(null)}
      footer={
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-2 text-ink-2">
            Declare new variable
          </div>
          <div className="flex items-end gap-2">
            <input
              value={draftName}
              onChange={(e) => {
                setDraftName(e.target.value);
                setAddError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') tryAdd();
              }}
              placeholder="variable_name"
              className="flex-1 px-3 py-2 rounded border border-cream-border bg-white text-sm font-mono"
            />
            <select
              value={draftType}
              onChange={(e) => setDraftType(e.target.value as VarType)}
              className="px-3 py-2 rounded border border-cream-border bg-white text-sm"
            >
              <option value="number">number</option>
              <option value="boolean">boolean</option>
              <option value="string">string</option>
            </select>
            <button
              onClick={tryAdd}
              className="px-4 py-2 rounded text-sm flex items-center gap-1.5 bg-ink text-cream-2"
            >
              <Plus size={14} /> Add
            </button>
          </div>
          {addError && (
            <div className="mt-2 text-xs flex items-center gap-1.5 text-terracotta-dark">
              <AlertCircle size={12} /> {addError}
            </div>
          )}
        </div>
      }
    >
      {entries.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {entries.map(([name, decl]) => (
            <VariableRow
              key={name}
              name={name}
              decl={decl}
              onUpdate={(patch) => updateVariable(name, patch)}
              onRename={(newName) => renameVariable(name, newName)}
              onDelete={() => {
                if (confirm(`Delete "${name}"? Every condition and effect that references it will be cleaned up.`)) {
                  deleteVariable(name);
                }
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

function EmptyState() {
  return (
    <div className="text-sm italic text-center py-8 px-4 rounded border border-dashed border-cream-border text-ink-2">
      <div className="font-bold not-italic mb-1 text-ink">No variables yet.</div>
      Variables let scenes and choices respond to learner state. Declare one below —
      for example, a <code className="not-italic bg-cream px-1.5 rounded">number</code> called <code className="not-italic bg-cream px-1.5 rounded">empathy</code> that choices can add to, or a <code className="not-italic bg-cream px-1.5 rounded">boolean</code> called <code className="not-italic bg-cream px-1.5 rounded">escalated</code> that gates a later scene.
    </div>
  );
}

interface VariableRowProps {
  name: string;
  decl: VariableDecl;
  onUpdate: (patch: Partial<VariableDecl>) => void;
  onRename: (newName: string) => { ok: true } | { ok: false; error: string };
  onDelete: () => void;
}

function VariableRow({ name, decl, onUpdate, onRename, onDelete }: VariableRowProps) {
  // Local state for the rename buffer so the user can type freely. The
  // global rename fires on blur or Enter, not on every keystroke.
  const [nameDraft, setNameDraft] = useState(name);
  const [nameError, setNameError] = useState('');

  // Resync when the canonical name changes (after rename succeeds).
  useEffect(() => {
    setNameDraft(name);
  }, [name]);

  const commitRename = () => {
    const next = nameDraft.trim();
    if (next === name) return;
    const result = onRename(next);
    if (!result.ok) {
      setNameError(result.error);
      setNameDraft(name);
    } else {
      setNameError('');
    }
  };

  const onTypeChange = (newType: VarType) => {
    onUpdate({ type: newType, default: defaultForType(newType) });
  };

  const onDefaultChange = (raw: string) => {
    if (decl.type === 'number') {
      const n = Number(raw);
      onUpdate({ default: Number.isFinite(n) ? n : 0 });
    } else if (decl.type === 'boolean') {
      onUpdate({ default: raw === 'true' });
    } else {
      onUpdate({ default: raw });
    }
  };

  return (
    <div className="rounded border p-3 border-cream-border bg-cream-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 grid grid-cols-[1.2fr_0.8fr_0.8fr] gap-2">
          <div>
            <label className="text-[9px] uppercase tracking-widest block mb-1 text-ink-2">
              Name
            </label>
            <input
              value={nameDraft}
              onChange={(e) => {
                setNameDraft(e.target.value);
                setNameError('');
              }}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              className="w-full px-2 py-1.5 rounded border bg-white text-sm font-mono"
              style={{ borderColor: nameError ? '#A14A38' : '#E5DFD0' }}
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest block mb-1 text-ink-2">
              Type
            </label>
            <select
              value={decl.type}
              onChange={(e) => onTypeChange(e.target.value as VarType)}
              className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm"
            >
              <option value="number">number</option>
              <option value="boolean">boolean</option>
              <option value="string">string</option>
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest block mb-1 text-ink-2">
              Default
            </label>
            {decl.type === 'boolean' ? (
              <select
                value={String(decl.default)}
                onChange={(e) => onDefaultChange(e.target.value)}
                className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm"
              >
                <option value="false">false</option>
                <option value="true">true</option>
              </select>
            ) : (
              <input
                type={decl.type === 'number' ? 'number' : 'text'}
                value={String(decl.default ?? '')}
                onChange={(e) => onDefaultChange(e.target.value)}
                className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm font-mono"
              />
            )}
          </div>
        </div>
        <button
          onClick={onDelete}
          className="mt-5 text-xs hover:underline self-start flex items-center gap-1 text-terracotta-dark"
          title="Delete this variable. References in conditions and effects will be stripped."
        >
          <Trash2 size={12} /> Remove
        </button>
      </div>
      <input
        value={decl.description || ''}
        onChange={(e) => onUpdate({ description: e.target.value })}
        placeholder="Description (visible to authors when picking this variable in rule builders)"
        className="w-full mt-2 px-2 py-1.5 rounded border border-cream-border bg-white text-xs italic text-ink-2"
      />
      {nameError && (
        <div className="text-xs mt-1 flex items-center gap-1 text-terracotta-dark">
          <AlertCircle size={11} /> {nameError}
        </div>
      )}
    </div>
  );
}
